import React, { useState, useEffect, useRef, useCallback } from "react";
import { Grid } from "@mui/material";
import styles from "./ModifyReservationPrice.module.scss";
import {
  TextValidator,
  ValidatorForm,
} from "react-material-ui-form-validator";
import { withTranslation } from "react-i18next";
import HasPermissionTo from "../../../../../common/HasPermissionTo";
import CheckBox from "../../../../../shared/checkBox/CheckBox";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import { PAYMENT_THRESHOLD_LIMIT } from "../../../../../common/Constants";

const POSTFIX_CURRENCY_MARKETS = ['VNM', 'THA'];

function ReservationList(props) {
  const [maxLimit, setMaxLimit] = useState(0);
  const [maxLimitFormatted, setMaxLimitFormatted] = useState("");
  const [validationMaxLimitMsg, setValidationMaxLimitMsg] = useState("");
  const [validationInvalidFormat, setValidationInvalidFormat] = useState("");
  const [validationRequired, setValidationRequired] = useState("");

  const priceRef = useRef();

  useEffect(() => {
    ValidatorForm.addValidationRule("changeInPrice", (value, model) => {
      let changeInPrice = true;
      if (props.originalDeposit && Array.isArray(props.originalDeposit)) {
        props.originalDeposit.forEach((item) => {
          changeInPrice = !(
            model === item.model && item.amount === parseFloat(value).toFixed(2)
          );
        });
      }
      if (typeof props.applyBtn === 'function') {
        props.applyBtn(changeInPrice);
      }
      return true;
    });

    const market = props.userDetails?.market;
    setMaxLimit(
      PAYMENT_THRESHOLD_LIMIT[market]?.maxLimit || 99999.99
    );
    setMaxLimitFormatted(
      PAYMENT_THRESHOLD_LIMIT[market]?.maxLimitFormatted || "99,999.99"
    );
    setValidationMaxLimitMsg(
      props.t("PaymentThreshold.validations.maxLimit") || ""
    );
    setValidationInvalidFormat(
      props.t("PaymentThreshold.validations.invalidFormat") || ""
    );
    setValidationRequired("Value is required");

    return () => {
      ValidatorForm.removeValidationRule("changeInPrice");
    };
  }, [props.userDetails, props.t, props.originalDeposit, props.applyBtn]);

  const displayDepositPrice = useCallback((item, defaultCurrency) => {
    if (item.amount !== null && (!!item.amount || item.amount >= 0)) {
      const formattedAmount = parseFloat(item.amount).toFixed(2);
      const symbol = defaultCurrency ? defaultCurrency.symbol : "";
      const market = props.userDetails?.market;

      if (POSTFIX_CURRENCY_MARKETS.includes(market)) {
        return (
          <div>
            {formattedAmount} {symbol}
          </div>
        );
      } else {
        return (
          <div>
            {symbol} {formattedAmount}
          </div>
        );
      }
    }
    return null;
  }, [props.userDetails]);

  const handleCheckboxChange = useCallback((event) => {
    if (typeof props.clickCheckBox === 'function') {
      props.clickCheckBox(event);
    } else {
      console.error("[ReservationList] clickCheckBox prop is not a function:", props.clickCheckBox);
    }
  }, [props.clickCheckBox]);

  const handleInputChange = useCallback((event) => {
    if (typeof props.updateReservation === 'function') {
      props.updateReservation(event);
    } else {
      console.error("[ReservationList] updateReservation prop is not a function:", props.updateReservation);
    }
  }, [props.updateReservation]);

  const handleEditReservations = useCallback(() => {
    if (typeof props.editReservations === 'function') {
      props.editReservations();
    } else {
      console.error("[ReservationList] editReservations prop is not a function:", props.editReservations);
    }
  }, [props.editReservations]);

  const handleCheckAllCheckBox = useCallback(() => {
    if (typeof props.checkAllCheckBox === 'function') {
      props.checkAllCheckBox();
    } else {
      console.error("[ReservationList] checkAllCheckBox prop is not a function:", props.checkAllCheckBox);
    }
  }, [props.checkAllCheckBox]);

  const { defaultCurrency } = props.localizedInfo || {
    defaultCurrency: { symbol: "" },
  };

  const reservations = props.reservations || [];
  const market = props.userDetails?.market;
  const isPostfixMarket = POSTFIX_CURRENCY_MARKETS.includes(market);

  return (
    <div id="reservationPrices">
      <Grid
        container
        spacing={0}
        className={`${styles.tableRowHeader} pagePaddingLeft pagePaddingRight`}
      >
        <Grid item md={8}>
          <span id="model" className={styles.reservationInputGridModel}>
            {props.user.userType !== "dealer" || props.fromQuickstart ? (
              <CheckBox
                id="checkAllBoxes"
                isDisabled={props.fromQuickstart}
                checked={props.allChecked}
                onChange={handleCheckAllCheckBox}
              />
            ) : (
              ""
            )}
            {props.t("ModifyReservationPrice.model")}
          </span>
        </Grid>
        <Grid item md={4}>
          <div className={styles.reservationInputGridRightCell}>
            {props.t("ModifyReservationPrice.deposit")}
            <HasPermissionTo
              permissions={props.user?.permissions?.rolePermissions}
              perform={["depositPrice:write"]}
              condition={typeof props.dealerCanEditReservationPrice === 'function' ? props.dealerCanEditReservationPrice() : true}
              render={() => (
                <div
                  className={`${styles.Edit} ${
                    props.editButtonDisable && styles.disabled
                  }`}
                  id="editReservations"
                  onClick={handleEditReservations}
                >
                  <div className={styles.RectangleStart}>&nbsp;</div>
                  <div className={styles.RectangleEnd}>&nbsp;</div>
                </div>
              )}
            />
          </div>
        </Grid>
      </Grid>
      <div>
        {reservations.map((item, index) => (
          <Grid
            container
            key={`${item.model}-${index}`}
            className={`${styles.tableRowContent} pagePaddingRight ${
              item.checked && styles.selectedRow
            }`}
            style={{
              flexWrap: "nowrap",
              background:
                item.newVehicleLine &&
                !item.checked &&
                "rgba(240, 153, 60, 0.1)",
            }}
          >
            <div className={styles.iconContainer}>
              {item.newVehicleLine && (
                <ReportProblemOutlinedIcon className={styles.icon} />
              )}
            </div>

            <Grid
              item
              md={9}
              className={`pagePaddingLeft ${styles.rowCellLeft}`}
            >
              {props.user.userType !== "dealer" || props.fromQuickstart ? (
                <div className={styles.checkbox}>
                  <CheckBox
                    id={`checkbox${index}`}
                    value={index}
                    checked={item.checked}
                    isDisabled={props.fromQuickstart}
                    onChange={handleCheckboxChange}
                  />
                </div>
              ) : (
                ""
              )}
              {item.model}
            </Grid>
            <Grid
              item
              md={3}
              className={`${styles.reservationInputGridRightCell} ${styles.validatorCell}`}
            >
              {props.editMode ? (
                <div className={styles.amountColumn}>
                  <div>
                    {!isPostfixMarket && defaultCurrency ? `${defaultCurrency.symbol} ` : ''}
                    &nbsp;&nbsp;
                  </div>
                  <TextValidator
                    autoComplete="off"
                    name={item.model}
                    ref={priceRef}
                    variant="outlined"
                    className={styles.inputField}
                    value={
                      item.amount === null || item.amount === ""
                        ? ""
                        : item.amount
                    }
                    onChange={handleInputChange}
                    validators={[
                      "required",
                      "matchRegexp:^[0-9]*(\\.[0-9]{0,2})?$",
                      "minNumber:0",
                      "maxNumber:" + maxLimit,
                      "changeInPrice:" + item.model,
                    ]}
                    errorMessages={[
                      validationRequired,
                      validationInvalidFormat,
                      validationInvalidFormat,
                      validationMaxLimitMsg + " " + maxLimitFormatted,
                      validationInvalidFormat,
                    ]}
                  />
                  <div>
                    {isPostfixMarket && defaultCurrency ? ` ${defaultCurrency.symbol}` : ''}
                  </div>
                </div>
              ) : (
                displayDepositPrice(item, defaultCurrency)
              )}
            </Grid>
          </Grid>
        ))}
      </div>
    </div>
  );
}

export default withTranslation("emp")(ReservationList);
